import React from 'react';
import { Edit2, Trash2, Eye, ChevronUp, ChevronDown } from 'lucide-react';
import styles from './WikiPagesManager.module.css';

const SortableTh = ({ label, field, sortBy, sortOrder, onSort, className }) => (
  <th className={className}>
    <button
      type="button"
      className={styles.sortableTh}
      onClick={() => onSort(field)}
      title={`Сортировать по ${label}`}
    >
      {label}
      {sortBy === field ? (
        sortOrder === 'asc' ? (
          <ChevronUp size={16} className={styles.sortIcon} aria-hidden />
        ) : (
          <ChevronDown size={16} className={styles.sortIcon} aria-hidden />
        )
      ) : null}
    </button>
  </th>
);

const WikiPagesTable = ({
  pages,
  sortBy,
  sortOrder,
  onSort,
  onEdit,
  onDelete,
  formatDate,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
}) => {
  const selectedSet = selectedIds instanceof Set ? selectedIds : new Set(selectedIds || []);
  const allSelected = pages.length > 0 && pages.every((p) => selectedSet.has(String(p.id)));
  const someSelected = pages.some((p) => selectedSet.has(String(p.id)));

  return (
    <>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.checkboxTh}>
                {pages.length > 0 && onToggleSelectAll && (
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => el && (el.indeterminate = someSelected && !allSelected)}
                    onChange={(e) => onToggleSelectAll(e.target.checked)}
                    aria-label="Выбрать все"
                  />
                )}
              </th>
              <SortableTh
                label="Название"
                field="title"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={onSort}
              />
              <th>Slug</th>
              <th>Автор</th>
              <SortableTh
                label="Создано"
                field="createdAt"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={onSort}
              />
              <SortableTh
                label="Обновлено"
                field="updatedAt"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={onSort}
              />
              <SortableTh
                label="Категория"
                field="category"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={onSort}
              />
              <th>Просмотры</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {pages.map((page) => (
              <tr key={page.id}>
                <td className={styles.checkboxTd}>
                  {onToggleSelect && (
                    <input
                      type="checkbox"
                      checked={selectedSet.has(String(page.id))}
                      onChange={() => onToggleSelect(page.id)}
                      aria-label={`Выбрать ${page.title || page.slug}`}
                    />
                  )}
                </td>
                <td className={styles.titleCell}>{page.title}</td>
                <td className={styles.slugCell}>{page.slug}</td>
                <td>
                  {page.author != null && String(page.author).trim() !== ''
                    ? page.author
                    : '—'}
                </td>
                <td>{formatDate(page.createdAt)}</td>
                <td>{formatDate(page.updatedAt)}</td>
                <td>{page.categoryDisplayName ?? page.category?.name ?? '—'}</td>
                <td>
                  <span className={styles.viewsBadge}>
                    <Eye size={14} />
                    {page.views ?? 0}
                  </span>
                </td>
                <td>
                  <div className={styles.actions}>
                    <button
                      type="button"
                      onClick={() => onEdit(page)}
                      className={styles.actionButton}
                      title="Редактировать"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(page)}
                      className={`${styles.actionButton} ${styles.deleteButton}`}
                      title="Удалить"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.mobileCardsWrapper}>
        <div className={styles.mobileCardsSelectAll}>
          {pages.length > 0 && onToggleSelectAll && (
            <label className={styles.mobileSelectAllLabel}>
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => el && (el.indeterminate = someSelected && !allSelected)}
                onChange={(e) => onToggleSelectAll(e.target.checked)}
                aria-label="Выбрать все"
              />
              <span>Выбрать все</span>
            </label>
          )}
        </div>

        <div className={styles.mobileCardsList}>
          {pages.map((page) => {
            const isSelected = selectedSet.has(String(page.id));
            const categoryName = page.categoryDisplayName ?? page.category?.name ?? '—';
            return (
              <div key={page.id} className={styles.mobileCard}>
                <div className={styles.mobileCardTop}>
                  {onToggleSelect && (
                    <label className={styles.mobileCheckbox}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onToggleSelect(page.id)}
                        aria-label={`Выбрать ${page.title || page.slug}`}
                      />
                    </label>
                  )}

                  <div className={styles.mobileCardInfo}>
                    <div className={styles.mobileCardTitle}>{page.title || page.slug}</div>
                    <div className={styles.mobileCardMeta}>
                      <span className={styles.mobileMetaRow}>
                        <span className={styles.mobileMetaLabel}>Категория:</span>{' '}
                        <span className={styles.mobileMetaValue}>{categoryName}</span>
                      </span>
                      <span className={styles.mobileMetaRow}>
                        <span className={styles.mobileMetaLabel}>Обновлено:</span>{' '}
                        <span className={styles.mobileMetaValue}>{formatDate(page.updatedAt)}</span>
                      </span>
                    </div>
                  </div>

                  <div className={styles.mobileCardActions}>
                    <button
                      type="button"
                      onClick={() => onEdit(page)}
                      className={styles.mobileActionButton}
                      title="Редактировать"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(page)}
                      className={`${styles.mobileActionButton} ${styles.mobileDeleteButton}`}
                      title="Удалить"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className={styles.mobileCardBottom}>
                  <span className={styles.viewsBadge}>
                    <Eye size={14} />
                    {page.views ?? 0}
                  </span>
                  <span className={styles.mobileSlug}>{page.slug}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default WikiPagesTable;
